// 
// Tide Protocol - Infrastructure for a TRUE Zero-Trust paradigm
// Copyright (C) 2022 Tide Foundation Ltd
// 
// This program is free software and is subject to the terms of 
// the Tide Community Open Code License as published by the 
// Tide Foundation Limited. You may modify it and redistribute 
// it in accordance with and subject to the terms of that License.
// This program is distributed WITHOUT WARRANTY of any kind, 
// including without any implied warranty of MERCHANTABILITY or 
// FITNESS FOR A PARTICULAR PURPOSE.
// See the Tide Community Open Code License for more details.
// You should have received a copy of the Tide Community Open 
// Code License along with this program.
// If not, see https://tide.org/licenses_tcoc2-0-0-en
//

import { Cryptide, Clients, Errors } from "@tideorg/js";

const { bytesToBase64 } = Cryptide.Serialization;
const Ed25519PublicComponent = Cryptide.Components.Schemes.Ed25519.Ed25519PublicComponent;
const { Point } = Cryptide.Ed25519;
const ClientBase = Clients.ClientBase;

import GenShardResponse from "../Models/KeyGen/GenShard/GenShardResponse.js";
import SetShardResponse from "../Models/KeyGen/SetShard/SetShardResponse.js";
import PrismConvertResponse from "../Models/KeyAuth/Convert/PrismConvertResponse.js";
import CMKConvertResponse from "../Models/KeyAuth/Convert/CMKConvertResponse.js";
import ConvertRememberedResponse from "../Models/KeyAuth/Convert/ConvertRememberedResponse.js";
import ReservationConfirmation from "../Models/Reservation/ReservationConfirmation.js";
import DeviceConvertResponse from "../Models/KeyAuth/Convert/DeviceConvertResponse.js";

export default class NodeClient extends ClientBase {
    /**
     * @param {string} url
     */
    constructor(url) {
        super(url)
        this.enabledTideDH = false;
    }

    async isActive() {
        const response = await this._get("/active", 400);
        const responseData = await this._handleError(response, "Is Active");
        return responseData;
    }

    /**
     * @param {string} uid 
     * @returns {OrkInfo[]}
     */
    async FindReservers(uid){
        const response = await this._get(`/Network/Authentication/Users/GetReservers/${uid}`);
        try{
            const responseData = await this._handleError(response, "Find Reservers");
            const formattedResponse = JSON.parse(responseData);
            if(formattedResponse.length == 0) throw new Errors.TideError({
                code: Errors.TideJsErrorCodes.VAL_UID_FORBIDDEN,
                displayMessage: "This username is not allowed. Please choose a different one.",
                source: "Ork/Enclave/js/Clients/NodeClient.js:57 (FindReservers empty-list)",
                details: [{
                    endpoint: "/Network/Authentication/Users/GetReservers",
                    displayMessage: `Reserver lookup returned empty list (no ORK is willing to reserve this uid). uid=${uid ? String(uid).slice(0, 16) + "..." : "<missing>"}`,
                }],
            });
            const returnedResponse = formattedResponse.map(orkEntry => OrkInfo.from(orkEntry));
            return returnedResponse;
        }catch(err){
            // Preserve TideErrors (and any other structured error) verbatim so the dev
            // console sees the full toString() block including source, code, and details.
            // The previous `throw Error(err)` stringified the underlying error and
            // discarded all structure.
            if (Errors.TideError.isTideError(err)) throw err;
            throw new Errors.TideError({
                code: Errors.TideJsErrorCodes.NET_FETCH_FAILED,
                displayMessage: "We couldn't reach the Tide network to look up that username. Please check your connection and try again.",
                source: "Ork/Enclave/js/Clients/NodeClient.js:74 (FindReservers catch-wrapper)",
                details: [{
                    endpoint: "/Network/Authentication/Users/GetReservers",
                    displayMessage: `FindReservers failed before producing a result. uid=${uid ? String(uid).slice(0, 16) + "..." : "<missing>"}`,
                }],
                cause: err,
            });
        }
    }

    async GetSomeORKs(){
        const response = await this._get('/Network/Authentication/Node/Some');
        const responseData = await this._handleError(response, "Get Some Orks");
        const formattedResponse = JSON.parse(responseData)
        const returnedResponse = formattedResponse.map(orkEntry => {
            return OrkInfo.from(orkEntry);
        });
        return returnedResponse;
    }

    async GetMaxORKs() {
        const response = await this._get('/Network/Authentication/Node/Max');
        const responseData = await this._handleError(response, "Get Max Orks");
        const formattedResponse = JSON.parse(responseData)
        const returnedResponse = formattedResponse.map(orkEntry => {
            return OrkInfo.from(orkEntry);
        });
        return returnedResponse;
    }

    /**
     * @param {number} index
     * @param {string} uid
     * @param {string} sessId
     * @param {string} voucher
     * @param {Point} gSessKeyPub
     * @returns 
     */
    async ReserveUID(index, uid, sessId, voucher, gSessKeyPub) {
        const data = this._createFormData({
            "sessId": sessId,
            "voucher": voucher,
            "gSessKeyPub": gSessKeyPub.toBase64()
        })
        const response = await this._post(`/Authentication/Create/ReserveUserId?uid=${uid}`, data);
        const responseData = await this._handleError(response, "Reserve UID");
        return {
            "index": index,
            resConf: ReservationConfirmation.from(responseData)
        };
    }

    /**
     * @param {number} index
     * @param {string} uid 
     * @param {Point} gBlurPass
     * @param {Ed25519PublicComponent} gSessKeyPub
     * @param {boolean} rememberMe
     * @param {boolean} cmkCommitted
     * @param {boolean} prismCommitted
     * @param {string} voucher
     * @param {string} m
     * @returns
     */
    async Convert(index, uid, gBlurPass, gSessKeyPub, rememberMe, voucher, m, cmkCommitted = true, prismCommitted = true) {
        const data = this._createFormData({
            'gBlurPass': gBlurPass.toBase64(),
            'gSessKeyPub': gSessKeyPub.Serialize().ToString(),
            'rememberMe': rememberMe,
            'cmkCommitted': cmkCommitted,
            'prismCommitted': prismCommitted,
            'voucher': voucher,
            'M': m
        })
        const response = await this._post(`/Authentication/Auth/Convert?uid=${uid}`, data)
        const responseData = await this._handleError(response, "Convert CMK/Prism");
        const returnObj = {
            "CMKConvertResponse": CMKConvertResponse.from(responseData.split("|")[0]),
            "PrismConvertResponse": PrismConvertResponse.from(responseData.split("|")[1])
        };
        return {
            "index": index,
            returnObj // only one value is allowed in indexed requests, apart from the index
        }
    }

    /**
     * @param {number} index
     * @param {string} uid 
     * @param {string} gSessKeyPub
     * @param {boolean} rememberMe
     * @param {boolean} cmkCommitted
     * @param {boolean} prismCommitted
     * @param {string} voucher
     * @param {string} m
     * @returns
     */
    async DeviceConvert(index, uid, gSessKeyPub, rememberMe, voucher, m, cmkCommitted = true, prismCommitted = true) {
        const data = this._createFormData({
            'gBlurPass': null,
            'gSessKeyPub': gSessKeyPub,
            'rememberMe': rememberMe,
            'cmkCommitted': cmkCommitted,
            'prismCommitted': prismCommitted,
            'voucher': voucher,
            'M': m
        })
        const response = await this._post(`/Authentication/Auth/Convert?uid=${uid}`, data)
        const responseData = await this._handleError(response, "Device Convert CMK/Prism");
        const returnObj = {
            "DeviceConvertResponse": DeviceConvertResponse.from(responseData)
        };
        return {
            "index": index,
            returnObj // only one value is allowed in indexed requests, apart from the index
        }
    }

    /**
     * @param {number} index 
     * @param {string} uid 
     * @param {Point} gBlurPass 
     * @param {Ed25519PublicComponent} gSessKeyPub 
     * @param {string} voucher
     * @param {string} m
     */
    async ConvertPass(index, uid, gBlurPass, gSessKeyPub, voucher, m) {
        const data = this._createFormData({
            'gBlurPass': gBlurPass.toBase64(),
            'gSessKeyPub': gSessKeyPub.Serialize().ToString(),
            'voucher': voucher,
            'M': m
        })
        const response = await this._post(`/Authentication/Auth/ConvertPass?uid=${uid}`, data)
        const responseData = await this._handleError(response, "ConvertPass CMK/Prism");
        return {
            "index": index,
            "ConvertPassResponse": PrismConvertResponse.from(responseData)
        }
    }

    /**
     * @param {number} index
     * @param {string} uid 
     * @param {string} selfRequesti
     * @param {string} voucher
     * @returns
     */
    async ConvertRemembered(index, uid, selfRequesti, voucher) {
        const data = this._createFormData({
            'selfRequesti': selfRequesti,
            'voucher': voucher
        })
        const response = await this._post(`/Authentication/Auth/ConvertRemembered?uid=${uid}`, data)
        const responseData = await this._handleError(response, "Convert Passwordless");
        return {
            "index": index,
            data: ConvertRememberedResponse.from(responseData)
        };
    }

    /**
     * @param {string} uid 
     * @param {string} selfRequesti
     * @param {bigint} blurHCMKMul
     * @param {Uint8Array} bitwise
     * @param {boolean} cmkCommitted
     * @param {boolean} prismCommitted
     * @returns {Promise<string>}
     */
    async Authenticate(uid, selfRequesti, blurHCMKMul, bitwise, cmkCommitted = true, prismCommitted = true) {
        const data = this._createFormData({
            'selfRequesti': selfRequesti,
            'blurHCMKMul': blurHCMKMul.toString(),
            'bitwise': bytesToBase64(bitwise),
            'cmkCommitted': cmkCommitted,
            'prismCommitted': prismCommitted
        })
        const response = await this._post(`/Authentication/Auth/Authenticate?uid=${uid}`, data)

        const encSig = await this._handleError(response, "Authenticate");
        return encSig;
    }

    /**
     * @param {string} uid 
     * @param {string} prkRequesti
     * @param {bigint} blurHCMKMul
     * @param {Uint8Array} bitwise
     * @returns {Promise<string>}
     */
    async DeviceAuthenticate(uid, prkRequesti, blurHCMKMul, bitwise) {
        const data = this._createFormData({
            'prkRequesti': prkRequesti,
            'blurHCMKMul': blurHCMKMul.toString(),
            'bitwise': bytesToBase64(bitwise)
        })
        const response = await this._post(`/Authentication/Auth/DeviceAuthenticate?uid=${uid}`, data)

        const encSig = await this._handleError(response, "Authenticate");
        return encSig;
    }

    /**
     * 
     * @param {string} uid 
     * @param {bigint} blurHCMKMul 
     * @param {Uint8Array} ORKsBitwise 
     */
    async AuthenticateRemembered(uid, blurHCMKMul, ORKsBitwise) {
        const data = this._createFormData({
            'blurHCMKMul': blurHCMKMul.toString(),
            'bitwise': bytesToBase64(ORKsBitwise),
        })
        const response = await this._post(`/Authentication/Auth/AuthenticateRemembered?uid=${uid}`, data)

        const encSig = await this._handleError(response, "Authenticate");
        return encSig;
    }

    /**
     * @param {number} index
     * @param {string} uid
     * @param {string} voucher
     * @param {string} reservationAuth
     * @param {string} purpose
     * @param {string[]} mIdORKij
     * @param {number} numKeys
     * @param {Point[]} gMultipliers
     * @param {Point} gSessKeyPub
     * @returns {Promise<GenShardResponse>}
     */
    async GenShard(index, uid, voucher, reservationAuth, purpose, mIdORKij, numKeys, gMultipliers, gSessKeyPub) {
        const data = this._createFormData(
            {
                'voucher': voucher,
                'reservationAuth': reservationAuth,
                'purpose': purpose,
                'mIdORKij': mIdORKij,
                'numKeys': numKeys,
                'gMultipliers': gMultipliers.map(p => p == null ? "" : new Ed25519PublicComponent(p).Serialize().ToString()),
                'gSessKeyPub': gSessKeyPub.toBase64()
            }
        );
        const response = await this._post(`/Authentication/Create/GenShard?uid=${uid}`, data);

        const responseData = await this._handleError(response, "GenShard");
        const responseModel = GenShardResponse.from(responseData);
        return {
            index,
            responseModel
        }
    }

    /**
     * @param {number} index
     * @param {string} uid
     * @param {string} purpose
     * @param {Point[]} gMultipliers
     * @param {string} auth
     * @param {Point} gSessKeyPub
     * @returns {Promise<GenShardResponse>}
     */
    async UpdateShard(index, uid, purpose, gMultipliers, auth, gSessKeyPub, tag) {
        const data = this._createFormData(
            {
                'purpose': purpose,
                'gMultipliers': gMultipliers.map(p => p == null ? "" : new Ed25519PublicComponent(p).Serialize().ToString()),
                'auth': auth,
                'gSessKeyPub': gSessKeyPub.toBase64()
            }
        );
        const response = await this._post(`/Authentication/Create/UpdateShard?uid=${uid}`, data);

        const responseData = await this._handleError(response, "UpdateShard");
        const responseModel = GenShardResponse.from(responseData);
        return {
            index,
            tag,
            responseModel
        }
    }

    /**
     * @param {string} uid 
     * @param {string[]} shares 
     * @param {string} encAuthi
     * @param {Point} gSessKeyPub
     * @param {string} keyType
     */
    async SetShard(uid, shares, encAuthi, gSessKeyPub, keyType) {
        const data = this._createFormData(
            {
                'yijCipher': shares,
                'encAuthi': encAuthi,
                'gSessKeyPub': gSessKeyPub.toBase64()
            });
        const response = await this._post(`/Authentication/Create/Set${keyType}?uid=${uid}`, data);

        const responseData = await this._handleError(response, "SendShard");
        return SetShardResponse.from(responseData);
    }


    /**
     * @param {string} uid 
     * @param {bigint} S 
     * @param {Point} gSessKeyPub 
     */
    async Commit(uid, S, gSessKeyPub) {
        const data = this._createFormData(
            {
                'S': S.toString(),
                'gSessKeyPub': gSessKeyPub.toBase64()
            }
        );
        const response = await this._post(`/Authentication/Create/Commit?uid=${uid}`, data);
        const responseData = await this._handleError(response, "Commit");
        if (responseData !== "Key Created") Promise.reject("Commit: Account creation failed");
    }
    

    /**
     * @param {number} i
     * @param {string} uid 
     * @param {Point} gSessKeyPub 
     * @param {bigint} channelId
     * @param {string} homeOrkUrl
     * @param {string} voucher
     */
    async RecoverAccount(i, uid, gSessKeyPub, channelId, homeOrkUrl, voucher) {

        const data = this._createFormData({
            'gSessKeyPub': gSessKeyPub.toBase64(),
            'homeOrkUrl': homeOrkUrl,
            'channelId': channelId.toString(),
            'voucher': voucher
        })

        const response = await this._post(`/Authentication/AccountRecovery/StartRecovery?aruid=${uid}`, data);
        const responseData = await this._handleError(response, "StartRecovery");
        if (responseData !== "Email sent successfully") throw Error("orks.failedToSendEmail");
        return {
            index: i,
            responseData
        }
    }

    async FinalizeAccountRecovery(uid, channelId) {
        const response = await this._post(`/Authentication/AccountRecovery/CleanUpSession?uid=${uid}&channelId=${channelId}`, {});
        const responseData = await this._handleError(response, "CleanUpRecovery");
        if (responseData !== "Session has been cleaned up") throw Error("orks.errorFinalizingAccountRecovery");
        return { responseData }
    }
}